import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Package, Eye } from 'lucide-react';

interface OnboardingSession {
  id: string;
  status: string;
  created_at: string;
}

interface OnboardingItem {
  id: string;
  status: string;
  catalog_id: string | null;
  deal_id: string | null;
  eligibility: any;
  catalog?: {
    name: string;
    description: string | null;
  } | null;
  deal?: {
    id: string;
    deal_name: string | null;
    contacts?: {
      id: string;
      first_name: string | null;
      last_name: string | null;
    } | null;
    owner?: {
      id: string;
      first_name: string | null;
      last_name: string | null;
      display_name: string | null;
    } | null;
  } | null;
  onboarding_sessions?: OnboardingSession[];
}

interface OnboardingListViewProps {
  items: OnboardingItem[];
  isLoading: boolean;
  onViewItem: (item: OnboardingItem) => void;
}

const getLatestSession = (sessions?: OnboardingSession[]) => {
  if (!sessions || sessions.length === 0) return null;
  return sessions.sort((a, b) => 
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )[0];
};

const getContactName = (deal?: OnboardingItem['deal']) => {
  if (!deal?.contacts) return 'Cliente não informado';
  const { first_name, last_name } = deal.contacts;
  return [first_name, last_name].filter(Boolean).join(' ') || 'Cliente não informado';
};

const getOwnerName = (deal?: OnboardingItem['deal']) => {
  if (!deal?.owner) return 'Não atribuído';
  const { display_name, first_name, last_name } = deal.owner;
  return display_name || [first_name, last_name].filter(Boolean).join(' ') || 'Não atribuído';
};

const getStatusBadge = (item: OnboardingItem) => {
  const latestSession = getLatestSession(item.onboarding_sessions);
  
  if (item.status === "pending_onboarding") {
    if (!latestSession || ['cancelled', 'completed'].includes(latestSession.status)) {
      return <Badge className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800">Aguardando</Badge>;
    }
    if (latestSession.status === 'scheduled') {
      return <Badge className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800">Agendado</Badge>;
    }
    if (latestSession.status === 'in_progress') {
      return <Badge className="bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800">Em Andamento</Badge>;
    }
  }
  
  if (item.status === "onboarded") {
    return <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800">Onboarded</Badge>;
  }
  
  if (item.status === "active") {
    return <Badge className="bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800">Ativo</Badge>;
  }
  
  return <Badge variant="outline">{item.status}</Badge>;
};

export const OnboardingListView: React.FC<OnboardingListViewProps> = ({
  items,
  isLoading,
  onViewItem,
}) => {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-14 rounded-lg" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <Package className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium mb-2">Nenhum item encontrado</h3>
        <p className="text-muted-foreground">
          Não há itens de onboarding para exibir.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Produto</TableHead>
            <TableHead>Cliente</TableHead>
            <TableHead className="hidden md:table-cell">Deal</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="hidden lg:table-cell">Vendedor</TableHead>
            <TableHead className="w-[80px]">Ação</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <TableRow 
              key={item.id} 
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => onViewItem(item)}
            >
              <TableCell>
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium truncate max-w-[150px]">
                    {item.catalog?.name || 'Produto não informado'}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                <span className="truncate max-w-[150px] block">
                  {getContactName(item.deal)}
                </span>
              </TableCell>
              <TableCell className="hidden md:table-cell">
                <span className="truncate max-w-[150px] block text-muted-foreground">
                  {item.deal?.deal_name || 'Deal não informado'}
                </span>
              </TableCell>
              <TableCell>
                {getStatusBadge(item)}
              </TableCell>
              <TableCell className="hidden lg:table-cell">
                <span className="truncate max-w-[120px] block text-muted-foreground">
                  {getOwnerName(item.deal)}
                </span>
              </TableCell>
              <TableCell>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewItem(item);
                  }}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
